/**
 * Simple page router for RPN Calculator
 * Replaces jQuery Mobile page navigation with CSS transitions
 */

export type PageId = 'calculator' | 'about' | 'preferences' | 'manual' | 'converter' | 'stack';

export class Router {
  private pages: Map<PageId, HTMLElement> = new Map();
  private currentPage: PageId = 'calculator';
  private onPageChange?: (pageId: PageId) => void;

  constructor(onPageChange?: (pageId: PageId) => void) {
    this.onPageChange = onPageChange;
  }

  /**
   * Register a page element
   */
  registerPage(id: PageId, element: HTMLElement): void {
    this.pages.set(id, element);

    // Set initial visibility
    if (id === this.currentPage) {
      element.classList.add('active');
      element.setAttribute('aria-hidden', 'false');
    } else {
      element.classList.remove('active');
      element.setAttribute('aria-hidden', 'true');
    }
  }

  /**
   * Navigate to a page
   */
  navigateTo(pageId: PageId): void {
    if (pageId === this.currentPage) return;

    const currentElement = this.pages.get(this.currentPage);
    const nextElement = this.pages.get(pageId);

    if (!nextElement) {
      console.warn(`Page "${pageId}" not found`);
      return;
    }

    // Hide current page
    if (currentElement) {
      currentElement.classList.remove('active');
      currentElement.classList.add('exiting');
      currentElement.setAttribute('aria-hidden', 'true');

      // Remove exiting class after animation
      setTimeout(() => {
        currentElement.classList.remove('exiting');
      }, 300);
    }

    // Show next page
    nextElement.classList.add('active', 'entering');
    nextElement.setAttribute('aria-hidden', 'false');

    // Remove entering class after animation
    setTimeout(() => {
      nextElement.classList.remove('entering');
    }, 300);

    this.currentPage = pageId;
    this.onPageChange?.(pageId);

    // Update URL hash for bookmarking
    if (pageId === 'calculator') {
      history.replaceState(null, '', window.location.pathname);
    } else {
      history.replaceState(null, '', `#${pageId}`);
    }
  }

  /**
   * Go back to calculator page
   */
  back(): void {
    this.navigateTo('calculator');
  }

  /**
   * Get current page ID
   */
  getCurrentPage(): PageId {
    return this.currentPage;
  }

  /**
   * Handle browser back/forward
   */
  handlePopState(): void {
    const hash = window.location.hash.slice(1);
    if (hash && this.pages.has(hash as PageId)) {
      this.navigateTo(hash as PageId);
    } else {
      this.navigateTo('calculator');
    }
  }

  /**
   * Initialize router with hash-based navigation
   */
  init(): void {
    // Handle initial hash
    const hash = window.location.hash.slice(1);
    if (hash && this.pages.has(hash as PageId)) {
      this.currentPage = hash as PageId;
      const element = this.pages.get(this.currentPage);
      if (element) {
        element.classList.add('active');
        element.setAttribute('aria-hidden', 'false');
      }
    }

    // Listen for popstate
    window.addEventListener('popstate', () => this.handlePopState());
  }
}
